class CrossSellPage extends HTMLElement {
  constructor() {
    super();
    this.designData = [];
    this.selectedDesign = null;
  }

  connectedCallback() {
    this.loadDesignData();

    // Check if in Shopify customizer
    this.isCustomizer = this.dataset.customizer === 'true';

    // Checkout breaks bfcache and causes a full reload on back navigation,
    // so sessionStorage is used to persist the already-added state across reloads
    this.alreadyAdded = !!sessionStorage.getItem('cross-sell-added');

    if (this.isCustomizer) {
      this.handleCustomizerPreview();
    } else {
      this.matchDesignFromURL();
    }
  }

  loadDesignData() {
    const designElements = this.querySelectorAll('[data-design-title]');
    
    for (const element of designElements) {
      this.designData.push({
        title: element.dataset.designTitle,
        variantId: element.dataset.variantId,
        sellingPlanId: element.dataset.sellingPlanId,
        barImage: element.dataset.barImage,
        logoImage: element.dataset.logoImage,
        description: element.dataset.designDescription,
      });
    }
  }

  handleCustomizerPreview() {
    // In customizer, just use the first available design as a preview
    if (this.designData.length > 0) {
      this.selectedDesign = this.designData[0];

      // Set a mock variant for preview
      const mockVariant = {
        available: true,
        price: 10000 // Mock price
      };
      
      this.populateContent(mockVariant);
      this.revealContent();
      // Don't attach event listeners in customizer
    }
  }

  matchDesignFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const designTitle = urlParams.get('design');

    if (!designTitle) {
      this.redirectToCheckout();
      return;
    }

    // Normalize spaces in the design title
    const normalizedDesignTitle = designTitle.replace(/\s+/g, ' ').trim();

    // Find matching design (case-insensitive, space-normalized)
    this.selectedDesign = this.designData.find(
      d => d.title.replace(/\s+/g, ' ').trim().toLowerCase() === normalizedDesignTitle.toLowerCase()
    );

    if (!this.selectedDesign) {
      const fallbackDesignTitle = this.dataset.fallbackDesign;

      if (fallbackDesignTitle) {
        this.selectedDesign = this.designData.find(
          d => d.title.replace(/\s+/g, ' ').trim().toLowerCase() === fallbackDesignTitle.toLowerCase()
        );
      }

      if (!this.selectedDesign) {
        this.redirectToCheckout();
        return;
      }
    }

    // Check variant availability
    this.checkAvailabilityAndReveal();
  }

  async checkAvailabilityAndReveal() {
    if (!this.selectedDesign.variantId) {
      this.redirectToCheckout();
      return;
    }

    try {
      const response = await fetch(`/variants/${this.selectedDesign.variantId}.js`);
      
      if (!response.ok) {
        this.redirectToCheckout();
        return;
      }

      const variant = await response.json();

      if (!variant.available) {
        this.redirectToCheckout();
        return;
      }

      // Variant is available, populate content and reveal
      this.populateContent(variant);
      this.revealContent();
      this.attachEventListeners();

    } catch {
      this.redirectToCheckout();
    }
  }

  populateContent(variant) {
    const barLayout = this.dataset.barLayout || 'single';
    const useLogoImage = barLayout === 'double' || barLayout === 'triple';
    const imageUrl = useLogoImage && this.selectedDesign.logoImage
      ? this.selectedDesign.logoImage
      : this.selectedDesign.barImage;


    const imageElements = this.querySelectorAll('img[data-bar-image]');

    if (imageElements.length && imageUrl) {
      for (const el of imageElements) {
        el.src = imageUrl;
        el.alt = this.selectedDesign.title;
      }
    }

    // Set price from selling plan
    const priceElement = this.querySelector('[data-price]');
    if (priceElement && variant.price) {
      // Format price based on shop currency
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.textContent = formattedPrice;
    }
  }

  formatMoney(cents) {
    const currencyCode = this.dataset.currentCurrency || 'GBP';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol'
    }).format(cents / 100);
  }

  revealContent() {
    this.style.opacity = '1';
  }

  redirectToCheckout() {
    window.location.replace('/checkout');
  }

  trackVwoEvent(eventName) {
    window.VWO = window.VWO || [];
    window.VWO.push(['track.revenueConversion', eventName]);
  }

  attachEventListeners() {
    const ctaButton = this.querySelector('[data-cta-button]');
    const skipLink = this.querySelector('.cross-sell__skip');

    // Clear the sessionStorage flag once the page has re-initialised after a back navigation,
    // so a genuine second visit to this page (new flow) works correctly
    if (this.alreadyAdded) sessionStorage.removeItem('cross-sell-added');

    if (ctaButton) {
      ctaButton.addEventListener('click', () => this.handleAddToCart());
    }

    if (skipLink) {
      skipLink.addEventListener('click', () => this.#trackVwoGoal('cross-sell-declined'));
    }
  }

  #trackVwoGoal(goalId) {
    if (typeof window._vis_opt_goal_conversion === 'function') {
      window._vis_opt_goal_conversion(goalId);
    }
  }

  async handleAddToCart() {
    const ctaButton = this.querySelector('[data-cta-button]');

    if (!ctaButton || ctaButton.disabled) return;

    // Already added (returned via back button) — go straight to checkout
    if (this.alreadyAdded) {
      window.location.href = '/checkout';
      return;
    }

    if (!this.selectedDesign.variantId) {
      alert('Product not available.');
      return;
    }

    // Disable button and show loading state
    ctaButton.disabled = true;
    const originalText = ctaButton.textContent;
    this.ctaOriginalText = originalText;
    ctaButton.textContent = 'Adding to cart...';

    try {
      const payload = {
        id: parseInt(this.selectedDesign.variantId),
        quantity: 1,
        properties: {
          _cross_sell: 'true'
        }
      };

      // Add selling plan if available
      if (this.selectedDesign.sellingPlanId) {
        payload.selling_plan = parseInt(this.selectedDesign.sellingPlanId);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to add product to cart');
      }

      // Success - persist state to sessionStorage so it survives the full reload
      // that happens when returning via back button from Shopify checkout
      sessionStorage.setItem('cross-sell-added', '1');
      this.#trackVwoGoal('cross-sell-accepted');
      window.location.href = '/checkout';

    } catch (error) {
      // Reset button state
      ctaButton.disabled = false;
      ctaButton.textContent = originalText;
      
      alert('There was an error adding the product to your cart. Please try again.');
    }
  }
}

customElements.define('cross-sell-page', CrossSellPage);
